import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../utils/api';
import EnrollmentWizard from './EnrollmentWizard';
import { useMobileNav } from '../hooks/useMobileNav';
import RowActions from '../components/RowActions';
import EnrollmentDocuments from '../components/EnrollmentDocuments';
import { SpecialNeedsAndDietPicker } from '../components/SpecialNeedsAndDietPicker';
import { EMPTY_SPECIAL_NEEDS, normalizeSpecialNeeds } from '../utils/childFields';
import { ChildCareFlags } from '../components/ChildCareFlags';
import { ageAndYearLabel, ukYearGroup } from '../utils/childAge';
import { TermsReacknowledgeBanner } from '../components/TermsReacknowledgeBanner';
import { EnrollmentPlanManager } from '../components/EnrollmentPlanManager';
import { MealsManager } from '../components/MealsManager';
import { MessageComposer } from '../components/MessageComposer';
import { MessagesInbox } from '../components/MessagesInbox';
import { PushPermissionBanner } from '../components/PushPermissionBanner';
import { EmailPreferencesCard } from '../components/EmailPreferencesCard';
import { NotificationPreferencesCard } from '../components/NotificationPreferencesCard';
import { ParentInvoicesSection } from '../components/ParentInvoicesSection';
import { PoliciesTab } from '../components/PoliciesTab';
import { MomentComments } from '../components/MomentComments';
import { BookingPaymentDialog } from '../components/BookingPaymentDialog';
import { PaymentReviewDialog } from '../components/PaymentReviewDialog';

function ParentDashboard({ user, organization, onLogout, onChangePassword }) {
  const mobileNav = useMobileNav();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('children');
  const [children, setChildren] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [moments, setMoments] = useState([]);
  // Moment detail modal — opens on card click; also hosts the comment thread.
  const [viewingMoment, setViewingMoment] = useState(null);
  // Booking payment dialog — opens after a PAYG parent completes a
  // booking so they can choose Mollie card checkout or Bank Transfer.
  // Holds a queue of bookings that still need paying (siblings booked
  // in a single batch may be more than one). Retained for single-item
  // legacy flows; multi-booking baskets now use the PaymentReviewDialog.
  const [paymentQueue, setPaymentQueue] = useState([]);
  const [activePayment, setActivePayment] = useState(null);
  // Grouped payment intent — when a batch of bookings requires payment
  // we build ONE payment intent and open a single itemised checkout so
  // the parent gets one Mollie charge, one receipt and one statement
  // line instead of one per booking.
  const [pendingIntent, setPendingIntent] = useState(null);
  // "Pay all outstanding" flow — parent clicks the OutstandingBalanceCard
  // → we create ONE intent covering every unpaid booking and open the
  // review dialog. Prevents double-click double-submission.
  const [settlingOutstanding, setSettlingOutstanding] = useState(false);
  const handleSettleOutstanding = async () => {
    const unpaid = (bookings || []).filter((b) => b.payment_status === 'pending_payment');
    if (unpaid.length === 0) return;
    setSettlingOutstanding(true);
    try {
      const intent = await api.createPaymentIntentForBookings(unpaid.map((b) => b._id));
      setPendingIntent(intent);
    } catch (err) {
      toast.error(err?.message || 'Could not build payment basket');
    } finally {
      setSettlingOutstanding(false);
    }
  };
  const [balance, setBalance] = useState(null);
  const [showChildModal, setShowChildModal] = useState(false);
  const [showEnrollmentWizard, setShowEnrollmentWizard] = useState(false);
  // Freshly-registered parent: Login.js / InviteAccept.js drop a
  // sessionStorage flag so we know to auto-open the Enrollment Wizard
  // (instead of stranding them on an empty Children tab) AND to render
  // a welcome banner + first-run checklist. Cleared after we honour it
  // once so a page refresh doesn't keep reopening the modal.
  const [isFirstRun, setIsFirstRun] = useState(() => {
    try {
      return sessionStorage.getItem('kw_just_registered') === '1';
    } catch (_) {
      return false;
    }
  });
  const [enrollments, setEnrollments] = useState([]);
  // Shared MessageComposer trigger — set with {childId, childName}
  // from any "Message nursery" button to open the dialog. Parent
  // messages must always carry a child_id (rule c).
  const [composerCtx, setComposerCtx] = useState(null);
  // Unread message count for the Messages tab badge — refreshed on mount
  // and after any compose/reply round-trip so the navigation stays in sync.
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedChild, setSelectedChild] = useState(null);  // legacy single-select (kept for Recurring Plans/Waitlist flows that haven't migrated yet)
  // Multi-child selection for the Book Sessions screen. Sibling families
  // can pick 2+ kids and book the same session for all of them in one
  // click — the backend `POST /api/bookings/batch` handles capacity,
  // waitlisting and ledger writes per-child.
  const [selectedChildIds, setSelectedChildIds] = useState([]);
  const [selectedServiceType, setSelectedServiceType] = useState('nursery_all_day');
  const [transportNotes, setTransportNotes] = useState('');
  // How far ahead the booking list looks. Default 180 days so we don't
  // hide holiday-club sessions generated for school breaks 3-6 months
  // out. If no sessions are returned in the default window we auto-widen
  // to 365 days once so parents never see a false "nothing scheduled"
  // when late-generated holiday-club dates exist further out.
  const [bookingHorizonDays, setBookingHorizonDays] = useState(180);
  const autoWidenedRef = React.useRef(false);
  const [autoWidened, setAutoWidened] = useState(false);

  // Hard-block flag: parents who self-registered on an org with
  // `require_parent_approval=true` cannot add children, start enrollments
  // or book sessions until an admin approves them. Mirrors the backend
  // 403 guard on POST /api/children, /api/enrollments, /api/bookings so
  // the UI never offers actions that would just fail.
  const isAwaitingApproval = (
    user?.role === 'parent'
    && (user?.approval_status === 'pending' || user?.approval_status === 'rejected')
  );
  const approvalBlockedTitle = user?.approval_status === 'rejected'
    ? 'Your account was not approved by your childcare provider administrator.'
    : 'Awaiting administrator approval before you can add children or start an enrollment.';

  useEffect(() => {
    loadData();
  }, [activeTab, bookingHorizonDays]);

  // Mollie return handler: when the parent comes back from a grouped
  // payment intent checkout, surface a single confirmation toast and
  // clean the URL so a refresh doesn't retrigger it. The webhook has
  // already flipped every booking in the intent to `paid` so the
  // Bookings tab will reflect the new state on `loadData()`.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const intentId = params.get('intent_paid');
      const singleBookingId = params.get('booking_paid');
      if (intentId) {
        api.getPaymentIntent(intentId).then((intent) => {
          const n = (intent.line_items || []).length;
          const total = Number(intent.total_amount || intent.total || 0);
          const symbol = (intent.currency || 'GBP') === 'GBP' ? '£' : '';
          if (intent.status === 'paid') {
            toast.success(
              `Payment received — ${n} booking${n === 1 ? '' : 's'} confirmed (${symbol}${total.toFixed(2)}).`,
            );
          } else {
            toast.info(
              'Payment is being processed — your bookings will update once confirmed.',
            );
          }
        }).catch(() => { /* silent — user still sees updated booking states */ });
        // Strip the query param so refreshes don't re-toast.
        params.delete('intent_paid');
        const clean = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
        window.history.replaceState({}, '', clean);
      } else if (singleBookingId) {
        toast.success('Payment received — booking confirmed.');
        params.delete('booking_paid');
        const clean = `${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`;
        window.history.replaceState({}, '', clean);
      }
    } catch (_) { /* non-fatal */ }
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Always load children
      const childrenData = await api.getChildren();
      setChildren(childrenData);
      
      // Always load enrollments for status display
      const enrollmentsData = await api.getEnrollments();
      setEnrollments(enrollmentsData);

      // For first-run parents we also load bookings unconditionally so
      // the FirstRunChecklist can flip step 3 to done the moment they
      // finish their first booking — otherwise `bookings` would only
      // populate when they navigate to the Bookings tab and step 3
      // would show as pending forever on the Children tab.
      // We also load bookings for everyone so the OutstandingBalanceBadge
      // can render on the Children tab when there are unpaid bookings.
      if (activeTab !== 'bookings') {
        try {
          const initialBookings = await api.getBookings();
          setBookings(initialBookings || []);
        } catch (_) { /* non-fatal */ }
      }

      if (activeTab === 'book') {
        // Load sessions across the user-selected horizon (default 180d).
        // Holiday-club sessions are typically generated for school
        // breaks 3-6+ months out, so a tight window silently hides them.
        const startDate = new Date().toISOString();
        const endDate = new Date(
          Date.now() + bookingHorizonDays * 24 * 60 * 60 * 1000
        ).toISOString();
        const sessionsData = await api.getSessionInstances(startDate, endDate);
        setSessions(sessionsData);
        // Auto-widen: if the current window returned 0 sessions and we
        // haven't already auto-widened this session, jump to 365 days
        // so late-scheduled holiday-club dates surface without the
        // parent having to hunt for the dropdown.
        if (
          (!sessionsData || sessionsData.length === 0)
          && bookingHorizonDays < 365
          && !autoWidenedRef.current
        ) {
          autoWidenedRef.current = true;
          setAutoWidened(true);
          setBookingHorizonDays(365);
        }
      } else if (activeTab === 'bookings') {
        const bookingsData = await api.getBookings();
        setBookings(bookingsData);
      } else if (activeTab === 'waitlist') {
        const waitlistData = await api.getWaitlist();
        setWaitlist(waitlistData);
      } else if (activeTab === 'notifications') {
        const notificationsData = await api.getNotifications();
        setNotifications(notificationsData);
      } else if (activeTab === 'moments') {
        const momentsData = await api.getMoments();
        setMoments(momentsData);
      } else if (activeTab === 'account') {
        const balanceData = await api.getBalance();
        setBalance(balanceData);
      }

      // Always refresh the unread-message count for the nav badge so it
      // stays accurate while the user navigates between tabs.
      try {
        const unread = await api.getCommunications({ unread_only: true });
        setUnreadMessageCount(Array.isArray(unread)
          ? unread.filter((m) => m.sent_by_user_id !== user?._id).length
          : 0);
      } catch (_) { /* non-fatal — fall back to 0 */ }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  // Auto-open the Enrollment Wizard for freshly-registered parents so
  // they don't get stranded on an empty Children tab wondering what to
  // do next. Runs once per session — the sessionStorage flag is cleared
  // as soon as we act on it (or the parent already has children, in
  // which case there's nothing to onboard).
  useEffect(() => {
    if (!isFirstRun) return;
    if (loading) return;                    // wait for real children[] to load
    if (isAwaitingApproval) return;         // blocked users can't add children
    if (children.length > 0) {
      // They already have kids (returning parent whose flag was set
      // spuriously) — just clear the flag, no auto-open.
      try { sessionStorage.removeItem('kw_just_registered'); } catch (_) {}
      return;
    }
    setShowEnrollmentWizard(true);
    try { sessionStorage.removeItem('kw_just_registered'); } catch (_) {}
    // We deliberately keep isFirstRun=true in local state so the welcome
    // banner + first-run checklist keep showing until the parent
    // completes their first booking (see FirstRunChecklist below). It's
    // cleared implicitly when the component unmounts.
  }, [isFirstRun, loading, children.length, isAwaitingApproval]);

  const handleEnrollmentComplete = () => {
    setShowEnrollmentWizard(false);
    setActiveTab('children');
    loadData();
  };


  const [editingChild, setEditingChild] = useState(null);
  // Local-only state for the Quick Add/Edit modal photo picker. We hold the
  // selected File until submit, then upload to S3 via /api/files/upload and
  // pass the resulting `photo_file_id` (server resolves a presigned URL).
  const [childPhotoFile, setChildPhotoFile] = useState(null);
  const [childPhotoPreview, setChildPhotoPreview] = useState(null); // object URL
  const [childPhotoUploading, setChildPhotoUploading] = useState(false);

  // Structured SEND + dietary state for the Quick Add/Edit modal. Seeded
  // from the editingChild when the modal opens so existing values prefill.
  const [childDietary, setChildDietary] = useState([]);
  const [childSpecialNeeds, setChildSpecialNeeds] = useState({ ...EMPTY_SPECIAL_NEEDS });

  // Re-seed structured state every time we (re)open the modal in edit mode.
  useEffect(() => {
    if (showChildModal) {
      setChildDietary(Array.isArray(editingChild?.dietary_requirements) ? editingChild.dietary_requirements : []);
      setChildSpecialNeeds(normalizeSpecialNeeds(editingChild?.special_needs));
    }
  }, [showChildModal, editingChild]);

  const resetChildPhotoState = () => {
    if (childPhotoPreview) {
      try { URL.revokeObjectURL(childPhotoPreview); } catch (_) { /* noop */ }
    }
    setChildPhotoFile(null);
    setChildPhotoPreview(null);
    setChildPhotoUploading(false);
  };

  const handleChildPhotoPick = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    // Basic client-side guardrails: ~5MB cap and image MIME only. Backend
    // re-validates against ALLOWED_MEDIA_MIMES anyway.
    if (!file.type.startsWith('image/')) {
      alert('Please pick an image file (PNG, JPEG or WebP).');
      e.target.value = '';
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Image is too large. Please choose a file under 5 MB.');
      e.target.value = '';
      return;
    }
    if (childPhotoPreview) {
      try { URL.revokeObjectURL(childPhotoPreview); } catch (_) { /* noop */ }
    }
    setChildPhotoFile(file);
    setChildPhotoPreview(URL.createObjectURL(file));
  };

  const closeChildModal = () => {
    setShowChildModal(false);
    setEditingChild(null);
    resetChildPhotoState();
  };

  const handleCreateChild = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    // Only persist a SpecialNeeds object when SEN is actually flagged — we
    // don't want a "no SEN" record to take up space (or accidentally show a
    // cleared flag in the UI).
    const persistedSpecialNeeds = childSpecialNeeds.has_sen ? childSpecialNeeds : null;
    const childData = {
      first_name: formData.get('first_name'),
      last_name: formData.get('last_name'),
      date_of_birth: new Date(formData.get('date_of_birth')).toISOString(),
      age_group: formData.get('age_group'),
      medical_notes: formData.get('medical_notes') || '',
      allergies: (formData.get('allergies') || '').split(',').map(s => s.trim()).filter(Boolean),
      dietary_requirements: childDietary,
      special_needs: persistedSpecialNeeds,
    };

    try {
      // If a new photo was picked, upload it first and pass photo_file_id
      // along with the rest of the payload. The backend will resolve a
      // fresh presigned URL into photo_url.
      if (childPhotoFile) {
        setChildPhotoUploading(true);
        const linkedId = editingChild ? editingChild._id : undefined;
        const uploadRes = await api.uploadFile(childPhotoFile, {
          category: 'generic',
          linkedType: 'child',
          ...(linkedId ? { linkedId } : {}),
        });
        childData.photo_file_id = uploadRes.file_id;
        childData.photo_url = uploadRes.url;
        setChildPhotoUploading(false);
      }

      if (editingChild) {
        await api.updateChild(editingChild._id, childData);
      } else {
        await api.createChild(childData);
      }
      closeChildModal();
      loadData();
    } catch (err) {
      setChildPhotoUploading(false);
      alert('Error saving child: ' + err.message);
    }
  };

  const handleArchiveChild = async (child, force = false) => {
    await api.deleteChild(child._id, force);
    loadData();
  };

  const handleBookSession = async (sessionId) => {
    if (!selectedChildIds || selectedChildIds.length === 0) {
      toast.error('Pick at least one child first.');
      return;
    }

    // Determine if transport is required based on service type
    const requiresTransport = selectedServiceType !== 'nursery_all_day' && 
                              selectedServiceType !== 'holiday_club';

    try {
      const result = await api.createBookingsBatch({
        session_instance_id: sessionId,
        child_ids: selectedChildIds,
        service_type: selectedServiceType,
        requires_transport: requiresTransport,
        transport_requirements: requiresTransport ? {
          service_type: selectedServiceType,
          special_instructions: transportNotes
        } : null
      });

      // Compose a single readable summary so a parent who booked
      // 3 kids in one go sees one toast, not three.
      const parts = [];
      if (result.confirmed?.length) {
        parts.push(`✓ ${result.confirmed.length} booked`);
      }
      if (result.waitlisted?.length) {
        parts.push(`⏳ ${result.waitlisted.length} waitlisted`);
      }
      if (result.errors?.length) {
        parts.push(`⚠️ ${result.errors.length} couldn’t book`);
      }
      const headline = parts.length ? parts.join(' · ') : 'Nothing changed';
      const totalLabel = result.total_charged
        ? ` — £${Number(result.total_charged).toFixed(2)}`
        : '';

      if (result.errors?.length && !result.confirmed?.length && !result.waitlisted?.length) {
        toast.error(`${headline}: ${result.errors[0].message}`);
      } else if (result.errors?.length) {
        toast.warning(`${headline}${totalLabel}. First issue: ${result.errors[0].message}`);
      } else if (result.confirmed?.length) {
        toast.success(`${headline}${totalLabel}.`);
      } else if (result.waitlisted?.length) {
        toast.info(headline);
      } else {
        toast(headline);
      }

      // Reset transport fields after a successful batch
      if (result.confirmed?.length || result.waitlisted?.length) {
        setSelectedServiceType('nursery_all_day');
        setTransportNotes('');
      }

      // Queue up any PAYG bookings that still need payment.
      const pendingPay = (result.confirmed || []).filter((c) => c.requires_payment);
      if (pendingPay.length === 1) {
        // Single booking — keep the legacy dialog for the "pay now" step.
        const [only] = pendingPay;
        setPaymentQueue([]);
        setActivePayment({
          _id: only.booking_id,
          payment_amount: only.price,
          child_name: only.child_name,
        });
      } else if (pendingPay.length > 1) {
        // Multi-booking batch → group into ONE payment intent so the
        // parent gets a single Mollie charge with itemised summary.
        try {
          const intent = await api.createPaymentIntentForBookings(
            pendingPay.map((c) => c.booking_id),
          );
          setPendingIntent(intent);
        } catch (intentErr) {
          // Fallback to per-booking queue so the parent can still pay
          // (older browsers / transient backend issues).
          toast.warning('Grouped checkout unavailable — paying one at a time.');
          const enriched = pendingPay.map((c) => ({
            _id: c.booking_id,
            payment_amount: c.price,
            child_name: c.child_name,
          }));
          setPaymentQueue(enriched.slice(1));
          setActivePayment(enriched[0]);
        }
      }

      loadData();
    } catch (err) {
      toast.error('Error booking: ' + err.message);
    }
  };

  const handleClaimWaitlist = async (notification) => {
    try {
      const result = await api.claimWaitlistSpot({
        session_instance_id: notification.data.session_instance_id,
        claim_token: notification.data.claim_token
      });

      if (result.status === 'claimed') {
        alert(`Spot claimed! Price: £${result.price.toFixed(2)}`);
        loadData();
      } else {
        alert(result.message);
      }
    } catch (err) {
      alert('Error claiming spot: ' + err.message);
    }
  };

  return (
    <div className="dashboard">
      <button
        type="button"
        className="mobile-nav-toggle"
        onClick={mobileNav.toggle}
        aria-label="Toggle navigation"
        data-testid="mobile-nav-toggle"
      >
        {mobileNav.isOpen ? '✕' : '☰'}
      </button>
      <div className={`mobile-nav-backdrop ${mobileNav.isOpen ? 'show' : ''}`} onClick={mobileNav.close}></div>
      <div className={`sidebar ${mobileNav.isOpen ? 'mobile-open' : ''}`} onClick={(e) => {
        if (e.target.closest('.nav-item')) mobileNav.close();
      }}>
        <div className="sidebar-header" data-testid="sidebar-header">
          {organization && organization.settings && organization.settings.logo_url ? (
            <>
              <img
                src={organization.settings.logo_url}
                alt={organization.name}
                style={{ maxHeight: 48, maxWidth: 160, objectFit: 'contain', marginBottom: '0.5rem' }}
                data-testid="sidebar-org-logo"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <h2 style={{ fontSize: '1rem', margin: 0 }}>🌈 {organization.name}</h2>
            </>
          ) : (
            <h2>🌈 {organization ? organization.name : 'Kids WiseApp'}</h2>
          )}
          <p>{user.full_name}</p>
        </div>

        <ul className="nav-menu">
          <li 
            className={`nav-item ${activeTab === 'children' ? 'active' : ''}`}
            onClick={() => setActiveTab('children')}
            data-testid="nav-children"
          >
            👶 My Children
          </li>
          <li 
            className={`nav-item ${activeTab === 'book' ? 'active' : ''}`}
            onClick={() => setActiveTab('book')}
            data-testid="nav-book"
          >
            📅 Book Sessions
          </li>
          <li 
            className={`nav-item ${activeTab === 'bookings' ? 'active' : ''}`}
            onClick={() => setActiveTab('bookings')}
            data-testid="nav-bookings"
          >
            ✅ My Bookings
          </li>
          <li
            className={`nav-item ${activeTab === 'invoices' ? 'active' : ''}`}
            onClick={() => setActiveTab('invoices')}
            data-testid="nav-invoices"
          >
            🧾 My Invoices
          </li>
          <li
            className={`nav-item ${activeTab === 'plans' ? 'active' : ''}`}
            onClick={() => setActiveTab('plans')}
            data-testid="nav-plans"
          >
            🔁 Recurring Plans
          </li>
          <li
            className={`nav-item ${activeTab === 'meals' ? 'active' : ''}`}
            onClick={() => setActiveTab('meals')}
            data-testid="nav-meals"
          >
            🍽️ Weekly Menu
          </li>
          <li 
            className={`nav-item ${activeTab === 'waitlist' ? 'active' : ''}`}
            onClick={() => setActiveTab('waitlist')}
            data-testid="nav-waitlist"
          >
            ⏳ Waitlist
          </li>
          <li 
            className={`nav-item ${activeTab === 'moments' ? 'active' : ''}`}
            onClick={() => setActiveTab('moments')}
            data-testid="nav-moments"
          >
            📸 Moments
          </li>
          <li
            className={`nav-item ${activeTab === 'messages' ? 'active' : ''}`}
            onClick={() => setActiveTab('messages')}
            data-testid="nav-messages"
          >
            ✉️ Messages {unreadMessageCount > 0 && `(${unreadMessageCount})`}
          </li>
          <li 
            className={`nav-item ${activeTab === 'notifications' ? 'active' : ''}`}
            onClick={() => setActiveTab('notifications')}
            data-testid="nav-notifications"
          >
            🔔 Notifications {notifications.filter(n => !n.is_read).length > 0 && `(${notifications.filter(n => !n.is_read).length})`}
          </li>
          <li 
            className={`nav-item ${activeTab === 'account' ? 'active' : ''}`}
            onClick={() => setActiveTab('account')}
            data-testid="nav-account"
          >
            💳 Account
          </li>
          <li
            className={`nav-item ${activeTab === 'policies' ? 'active' : ''}`}
            onClick={() => setActiveTab('policies')}
            data-testid="nav-policies"
            title="Read the nursery's fees, cancellation, refund and safeguarding policies"
          >
            📄 Policies
          </li>
          <li
            className="nav-item"
            onClick={onChangePassword}
            data-testid="nav-change-password"
            title="Change your password"
          >
            🔐 Change password
          </li>
          <li 
            className="nav-item logout-btn" 
            onClick={onLogout}
            data-testid="logout-btn"
          >
            🚪 Logout
          </li>
        </ul>
      </div>

      <div className="main-content">
        {/* Terms re-acknowledgement banner — shown when the org admin
            has materially updated the Terms & Conditions document since
            this parent last accepted them. The user object carries a
            `terms_status` block computed by the backend on /api/auth/me.
            We let the parent quickly re-confirm here without forcing a
            full re-enrollment. */}
        <TermsReacknowledgeBanner user={user} organization={organization} onAcknowledged={() => window.location.reload()} />
        <PushPermissionBanner />

        {/* Pending approval banner — shown when this parent has self-registered
            via a branded /login/{slug} flow on an org that requires manual
            admin approval (settings.require_parent_approval = true). The
            account stays logged in but key actions (booking, enrollment) are
            gated until an admin approves the account in the Users tab. */}
        {user && user.role === 'parent'
          && (user.approval_status === 'pending' || user.approval_status === 'rejected') && (
          <div
            className="card"
            data-testid="parent-approval-banner"
            style={{
              marginBottom: '1.25rem',
              borderColor: user.approval_status === 'rejected' ? 'hsl(0 70% 75%)' : 'hsl(45 95% 65%)',
              background: user.approval_status === 'rejected' ? 'hsl(0 70% 96%)' : 'hsl(45 95% 94%)',
            }}
          >
            <h3 style={{ margin: 0 }}>
              {user.approval_status === 'rejected'
                ? '❌ Your account was not approved'
                : '⏳ Pending admin approval'}
            </h3>
            <p style={{ margin: '0.4rem 0 0', fontSize: '0.9rem', color: 'hsl(var(--muted-foreground))' }}>
              {user.approval_status === 'rejected' ? (
                <>Your account was reviewed but not approved by your childcare provider administrator. Please contact <strong>{organization?.name || 'your childcare provider'}</strong> for next steps.</>
              ) : (
                <>Thanks for joining <strong>{organization?.name || 'your childcare provider'}</strong>! An administrator will review and approve your account shortly. You'll be able to book sessions and start an enrollment once approved.</>
              )}
            </p>
          </div>
        )}

        {activeTab === 'children' && (
          <>
            {/* First-run welcome + 3-step checklist — shown only for
                freshly-registered parents until they've completed their
                first booking. Auto-hides otherwise so returning parents
                never see it. */}
            {isFirstRun && !isAwaitingApproval && (
              <FirstRunChecklist
                userName={user?.full_name}
                organizationName={organization?.name}
                hasChild={children.length > 0}
                hasBooking={bookings.length > 0}
                onAddChild={() => setShowEnrollmentWizard(true)}
                onBook={() => setActiveTab('book')}
                onDismiss={() => setIsFirstRun(false)}
              />
            )}

            <div className="page-header">
              <h1>My Children</h1>
              <button
                className="btn btn-primary"
                onClick={() => setShowEnrollmentWizard(true)}
                data-testid="start-enrollment-btn"
                disabled={isAwaitingApproval}
                title={isAwaitingApproval ? approvalBlockedTitle : 'Open the enrollment wizard to add a child'}
                style={isAwaitingApproval ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
              >
                {children.length === 0 ? '+ Add Your Child' : '+ Add Another Child'}
              </button>
            </div>

            {/* Outstanding balance badge — small nudge on Overview so
                parents notice unpaid bookings without switching tabs. */}
            <OutstandingBalanceBadge
              bookings={bookings}
              onSettle={handleSettleOutstanding}
              settling={settlingOutstanding}
              onJump={() => setActiveTab('bookings')}
            />

            {children.length > 0 && (
              <p
                style={{
                  marginTop: '-0.5rem',
                  marginBottom: '1rem',
                  fontSize: '0.875rem',
                  color: 'hsl(var(--muted-foreground))',
                }}
                data-testid="children-add-helper"
              >
                Need to register another child? Tap <strong>+ Add Another Child</strong> above to start a new enrollment (Terms &amp; Conditions and signature required).
              </p>
            )}

            {/* Enrolments — pending, rejected, AND approved so parents
                can always retrieve their signed copy. */}
            {enrollments.length > 0 && (
              <div style={{ marginBottom: '2rem' }}>
                <h3 style={{ marginBottom: '1rem' }}>Enrollment Applications</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {enrollments
                    .map(enrollment => {
                      const statusColors = {
                        draft: 'hsl(var(--muted-foreground))',
                        submitted: 'hsl(220 90% 60%)',
                        under_review: 'hsl(45 90% 60%)',
                        rejected: 'hsl(0 70% 60%)',
                        approved: 'hsl(150 60% 45%)',
                      };
                      
                      return (
                        <div 
                          key={enrollment._id || enrollment.id} 
                          className="card"
                          style={{ borderLeft: `4px solid ${statusColors[enrollment.status]}` }}
                          data-testid={`enrollment-${enrollment.status}`}
                        >
                          <div>
                            <h3 style={{ margin: '0 0 0.5rem 0' }}>
                              {enrollment.child_first_name} {enrollment.child_last_name}
                            </h3>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                              <span 
                                className="badge" 
                                style={{ 
                                  background: statusColors[enrollment.status],
                                  color: 'white',
                                  textTransform: 'capitalize'
                                }}
                              >
                                {enrollment.status.replace('_', ' ')}
                              </span>
                              {enrollment.submitted_at && (
                                <span style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
                                  Submitted {new Date(enrollment.submitted_at).toLocaleDateString()}
                                </span>
                              )}
                              {/* The signed enrolment is only useful once
                                  the parent has formally submitted it (so
                                  the OFSTED consents + signature exist). */}
                              {enrollment.status !== 'draft' && (
                                <button
                                  type="button"
                                  className="btn btn-ghost"
                                  onClick={() => window.open(api.getEnrollmentPrintableUrl(enrollment._id), '_blank', 'noopener')}
                                  data-testid={`enrollment-${enrollment._id}-print`}
                                  style={{ padding: '0.25rem 0.6rem', fontSize: '0.78rem' }}
                                >
                                  📄 View / Download signed enrolment
                                </button>
                              )}
                            </div>
                            {enrollment.status === 'rejected' && enrollment.rejection_reason && (
                              <div style={{ 
                                marginTop: '1rem', 
                                padding: '0.75rem', 
                                background: 'hsl(0 70% 95%)',
                                borderRadius: '4px'
                              }}>
                                <strong>Reason:</strong> {enrollment.rejection_reason}
                              </div>
                            )}
                            <EnrollmentDocuments
                              enrollment={enrollment}
                              readOnly={enrollment.status === 'approved' || enrollment.status === 'rejected'}
                              onChanged={(updated) => {
                                setEnrollments((prev) => prev.map((e) =>
                                  e._id === updated._id ? updated : e
                                ));
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {loading ? (
              <div className="loading">Loading children...</div>
            ) : children.length > 0 ? (
              <div className="card-grid">
                {children.map(child => (
                  <div key={child._id} className="card" style={{ cursor: 'pointer', transition: 'transform 0.2s', ':hover': { transform: 'translateY(-4px)' } }} onClick={() => navigate(`/child/${child._id}`)} data-testid={`child-card-${child._id}`}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                      <div>
                        <h3 style={{ margin: '0 0 0.5rem 0' }}>{child.first_name} {child.last_name}</h3>
                        {(() => {
                          const yg = ukYearGroup(child.date_of_birth);
                          const ageLbl = ageAndYearLabel(child.date_of_birth);
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              {ageLbl && (
                                <span style={{ fontSize: '0.78rem', color: 'hsl(var(--muted-foreground))' }} data-testid={`pcard-${child._id}-age-year`}>
                                  {ageLbl}
                                </span>
                              )}
                              {yg ? (
                                <span className="badge badge-blue" style={{ alignSelf: 'flex-start' }}>{yg.label}</span>
                              ) : (
                                <span className="badge badge-blue">{child.age_group}</span>
                              )}
                              {child.school_info?.school_name && (
                                <small style={{ fontSize: '0.72rem', color: 'hsl(var(--muted-foreground))' }} data-testid={`pcard-${child._id}-school`}>
                                  🏫 {child.school_info.school_name}
                                  {child.school_info.school_class ? ` · ${child.school_info.school_class}` : ''}
                                </small>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                      {child.photo_url && (
                        <img 
                          src={child.photo_url} 
                          alt={child.first_name}
                          style={{ width: '60px', height: '60px', borderRadius: '50%', objectFit: 'cover' }}
                        />
                      )}
                    </div>

                    {/* SEND / dietary / allergy badges in one consistent row.
                        Hide the legacy allergies block below since this
                        already renders allergies (avoids duplication). */}
                    {(child.allergies?.length > 0 || child.dietary_requirements?.length > 0 || child.special_needs?.has_sen) && (
                      <div style={{ marginBottom: '0.85rem' }}>
                        <ChildCareFlags child={child} testIdPrefix={`pcard-${child._id}-`} />
                      </div>
                    )}

                    {child.medical_notes && (
                      <div style={{ marginBottom: '1rem', padding: '0.75rem', background: 'hsl(var(--muted))', borderRadius: '6px' }}>
                        <div style={{ fontSize: '0.875rem' }}>
                          <strong>Medical:</strong> {child.medical_notes}
                        </div>
                      </div>
                    )}

                    <button 
                      className="btn btn-primary" 
                      style={{ width: '100%' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/child/${child._id}`);
                      }}
                      data-testid={`view-profile-${child._id}`}
                    >
                      View Full Profile →
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      style={{ width: '100%', marginTop: '0.4rem', fontSize: '0.85rem' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setComposerCtx({
                          childId: child._id,
                          childName: `${child.first_name} ${child.last_name}`.trim(),
                        });
                      }}
                      data-testid={`message-nursery-${child._id}`}
                    >
                      ✉️ Message provider
                    </button>
                    <div className="mt-3 flex justify-center" onClick={(e) => e.stopPropagation()}>
                      <RowActions
                        testIdPrefix={`parent-child-${child._id}`}
                        onEdit={() => { setEditingChild(child); setShowChildModal(true); }}
                        onDelete={(force) => handleArchiveChild(child, force)}
                        dangerLabel="Archive"
                        confirmTitle={`Archive ${child.first_name}?`}
                        confirmText="Children with bookings or moments are archived (soft-deleted) so the historical record stays intact. They will be hidden from your active list."
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <p>No children registered yet — let's add your child so you can start booking sessions.</p>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowEnrollmentWizard(true)}
                  disabled={isAwaitingApproval}
                  title={isAwaitingApproval ? approvalBlockedTitle : undefined}
                  style={isAwaitingApproval ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
                  data-testid="empty-add-child-btn"
                >
                  + Add Your Child
                </button>
                <p style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.6rem' }}>
                  Adding a child requires accepting Terms & Conditions and signing the parent declaration — takes about 3 minutes.
                </p>
              </div>
            )}
          </>
        )}

        {activeTab === 'book' && (
          <>
            <div className="page-header">
              <h1>Book Sessions</h1>
              <p>Browse and book available sessions</p>
            </div>

            {children.length === 0 ? (
              <div className="card">
                <div className="empty-state">
                  <p>Please add your first child before booking sessions — takes about 3 minutes.</p>
                  <button 
                    className="btn btn-primary" 
                    onClick={() => setShowEnrollmentWizard(true)}
                    data-testid="add-child-btn"
                    disabled={isAwaitingApproval}
                    title={isAwaitingApproval ? approvalBlockedTitle : undefined}
                    style={isAwaitingApproval ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
                  >
                    + Add Your Child
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="card" style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem' }}>
                    <div style={{ flex: 1, minWidth: '280px' }} data-testid="multi-child-picker">
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                        Book for{children.length > 1 ? ' (tick one or more children)' : ''}
                      </label>
                      {children.length === 0 ? (
                        <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
                          You haven't added any children yet. Use "Add Another Child" to start.
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                          {children.map(child => {
                            const checked = selectedChildIds.includes(child._id);
                            return (
                              <label
                                key={child._id}
                                data-testid={`child-checkbox-label-${child._id}`}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '0.5rem',
                                  padding: '0.4rem 0.75rem',
                                  border: '1px solid hsl(var(--border))',
                                  borderRadius: '999px',
                                  cursor: 'pointer',
                                  userSelect: 'none',
                                  background: checked ? 'hsl(var(--accent))' : 'hsl(var(--background))',
                                  fontWeight: checked ? 600 : 400,
                                  transition: 'background 120ms ease',
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) => {
                                    setSelectedChildIds(prev => {
                                      if (e.target.checked) {
                                        if (prev.includes(child._id)) return prev;
                                        return [...prev, child._id];
                                      }
                                      return prev.filter(id => id !== child._id);
                                    });
                                    // Keep legacy `selectedChild` in sync for any
                                    // single-child callers (waitlist claim etc.).
                                    setSelectedChild(child._id);
                                  }}
                                  data-testid={`child-checkbox-${child._id}`}
                                />
                                <span>{child.first_name} {child.last_name}</span>
                              </label>
                            );
                          })}
                          {children.length > 1 && (
                            <div style={{ width: '100%', marginTop: '0.4rem', display: 'flex', gap: '0.5rem' }}>
                              <button
                                type="button"
                                className="btn btn-secondary"
                                style={{ fontSize: '0.75rem', padding: '0.2rem 0.55rem' }}
                                onClick={() => setSelectedChildIds(children.map(c => c._id))}
                                data-testid="select-all-children-btn"
                              >
                                Select all
                              </button>
                              <button
                                type="button"
                                className="btn btn-secondary"
                                style={{ fontSize: '0.75rem', padding: '0.2rem 0.55rem' }}
                                onClick={() => setSelectedChildIds([])}
                                data-testid="clear-all-children-btn"
                              >
                                Clear
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <button 
                      className="btn btn-secondary" 
                      onClick={() => setShowEnrollmentWizard(true)}
                      disabled={isAwaitingApproval}
                      title={isAwaitingApproval ? approvalBlockedTitle : 'Start a new enrollment to add another child'}
                      style={isAwaitingApproval ? { opacity: 0.55, cursor: 'not-allowed' } : undefined}
                      data-testid="add-another-child-btn"
                    >
                      + Add Another Child (Enrollment)
                    </button>
                  </div>
                </div>

                {/* Service Type Selection */}
                <div className="card" style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ marginBottom: '1rem' }}>📚 Service Type</h3>
                  <p style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', marginBottom: '1rem' }}>
                    Select the type of childcare service you need. Transport options are included for school-age services.
                  </p>
                  
                  <div className="form-group" style={{ marginBottom: '1rem' }}>
                    <label>Service Type</label>
                    <select 
                      value={selectedServiceType} 
                      onChange={(e) => setSelectedServiceType(e.target.value)}
                      data-testid="service-type-select"
                      style={{ width: '100%' }}
                    >
                      <option value="nursery_all_day">Nursery All Day (No Transport)</option>
                      <option value="home_to_school">Home Collection + School Drop-off</option>
                      <option value="breakfast_club">Breakfast Club + School Drop-off</option>
                      <option value="afterschool_club">School Collection + Afterschool Club</option>
                      <option value="school_collection_only">School Collection → Home Direct</option>
                      <option value="afterschool_plus_home">Afterschool Club + Home Drop-off</option>
                      <option value="full_package">Full Package (Home → School → Club → Home)</option>
                      <option value="holiday_club">Holiday Club (All Ages, No Transport)</option>
                    </select>
                  </div>

                  {/* Transport Info Display */}
                  {selectedServiceType !== 'nursery_all_day' && selectedServiceType !== 'holiday_club' && (
                    <div style={{ 
                      padding: '1rem', 
                      background: 'hsl(var(--accent))', 
                      borderRadius: '8px',
                      marginBottom: '1rem'
                    }}>
                      <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem', fontWeight: '600' }}>
                        🚐 Transport Included
                      </h4>
                      <ul style={{ 
                        margin: 0, 
                        paddingLeft: '1.25rem', 
                        fontSize: '0.875rem',
                        lineHeight: '1.6'
                      }}>
                        {selectedServiceType === 'home_to_school' && (
                          <>
                            <li>Morning: Home collection → School drop-off</li>
                            <li>Route type: Home-to-School</li>
                          </>
                        )}
                        {selectedServiceType === 'breakfast_club' && (
                          <>
                            <li>Morning: Breakfast at centre → School drop-off</li>
                            <li>Route type: Club-to-School</li>
                          </>
                        )}
                        {selectedServiceType === 'afterschool_club' && (
                          <>
                            <li>Afternoon: School collection → Club activities</li>
                            <li>Route type: School-to-Club</li>
                          </>
                        )}
                        {selectedServiceType === 'school_collection_only' && (
                          <>
                            <li>Afternoon: School collection → Home direct</li>
                            <li>Route type: School-to-Home</li>
                          </>
                        )}
                        {selectedServiceType === 'afterschool_plus_home' && (
                          <>
                            <li>Afternoon: School collection → Club activities</li>
                            <li>Evening: Club → Home drop-off</li>
                            <li>Route types: School-to-Club, Club-to-Home</li>
                          </>
                        )}
                        {selectedServiceType === 'full_package' && (
                          <>
                            <li>Morning: Home collection → School drop-off</li>
                            <li>Afternoon: School collection → Club activities</li>
                            <li>Evening: Club → Home drop-off</li>
                            <li>Route types: All 3 daily routes</li>
                          </>
                        )}
                      </ul>
                    </div>
                  )}

                  {/* Transport Special Instructions */}
                  {selectedServiceType !== 'nursery_all_day' && selectedServiceType !== 'holiday_club' && (
                    <div className="form-group">
                      <label>Transport Notes (Optional)</label>
                      <textarea
                        value={transportNotes}
                        onChange={(e) => setTransportNotes(e.target.value)}
                        placeholder="e.g., Collection from side gate, notify teacher Mrs. Smith, etc."
                        rows="3"
                        data-testid="transport-notes"
                        style={{ width: '100%' }}
                      />
                    </div>
                  )}
                </div>

                {loading ? (
                  <div>Loading sessions...</div>
                ) : sessions.length > 0 ? (
                  <div className="card">
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '0.75rem',
                        marginBottom: '1rem',
                      }}
                    >
                      <h2 style={{ margin: 0 }}>
                        Available Sessions{' '}
                        <span style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))', fontWeight: 400 }}>
                          ({sessions.length} found in the next {bookingHorizonDays} days
                          {autoWidened && bookingHorizonDays === 365 ? ' — widened automatically' : ''})
                        </span>
                      </h2>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <label htmlFor="booking-horizon" style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
                          Show:
                        </label>
                        <select
                          id="booking-horizon"
                          value={bookingHorizonDays}
                          onChange={(e) => setBookingHorizonDays(Number(e.target.value))}
                          data-testid="booking-horizon-select"
                          style={{
                            padding: '0.35rem 0.55rem',
                            fontSize: '0.875rem',
                            borderRadius: '6px',
                            border: '1px solid hsl(var(--border))',
                            background: 'hsl(var(--background))',
                          }}
                        >
                          <option value={30}>Next 30 days</option>
                          <option value={60}>Next 60 days</option>
                          <option value={90}>Next 3 months</option>
                          <option value={180}>Next 6 months</option>
                          <option value={365}>Next 12 months</option>
                        </select>
                      </div>
                    </div>
                    <table>
                      <thead>
                        <tr>
                          <th>Session</th>
                          <th>Date</th>
                          <th>Time</th>
                          <th>Available</th>
                          <th>Price</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sessions.map(session => (
                          <tr key={session._id}>
                            <td>{session.template_name}</td>
                            <td>{new Date(session.date).toLocaleDateString()}</td>
                            <td>{session.start_time} - {session.end_time}</td>
                            <td>
                              <span className={`badge ${session.available > 5 ? 'badge-green' : session.available > 0 ? 'badge-yellow' : 'badge-red'}`}>
                                {session.capacity - session.booked_count} / {session.capacity}
                              </span>
                            </td>
                            <td>£{session.base_price}</td>
                            <td>
                              <button 
                                className="btn btn-primary" 
                                onClick={() => handleBookSession(session._id)}
                                disabled={selectedChildIds.length === 0}
                                title={selectedChildIds.length === 0 ? 'Pick at least one child above' : ''}
                                data-testid={`book-session-${session._id}`}
                              >
                                {session.available > 0
                                  ? (selectedChildIds.length > 1 ? `Book for ${selectedChildIds.length}` : 'Book')
                                  : 'Join Waitlist'}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="card">
                    <div className="empty-state" data-testid="no-sessions-empty">
                      <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>📅</div>
                      <h3 style={{ marginBottom: '0.4rem' }}>No sessions available</h3>
                      <p style={{ color: 'hsl(var(--muted-foreground))', marginBottom: '0.75rem' }}>
                        Nothing's scheduled in the next {bookingHorizonDays} days. Holiday-club
                        sessions are sometimes scheduled months in advance — try a longer horizon.
                      </p>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
                        <label htmlFor="booking-horizon-empty" style={{ fontSize: '0.875rem', alignSelf: 'center' }}>
                          Look ahead:
                        </label>
                        <select
                          id="booking-horizon-empty"
                          value={bookingHorizonDays}
                          onChange={(e) => setBookingHorizonDays(Number(e.target.value))}
                          data-testid="booking-horizon-select-empty"
                          style={{
                            padding: '0.35rem 0.55rem',
                            fontSize: '0.875rem',
                            borderRadius: '6px',
                            border: '1px solid hsl(var(--border))',
                            background: 'hsl(var(--background))',
                          }}
                        >
                          <option value={30}>Next 30 days</option>
                          <option value={60}>Next 60 days</option>
                          <option value={90}>Next 3 months</option>
                          <option value={180}>Next 6 months</option>
                          <option value={365}>Next 12 months</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}

        {activeTab === 'invoices' && (
          <>
            <div className="page-header">
              <h1>My Invoices</h1>
              <p>Invoices issued by the nursery — download PDFs and see what's outstanding.</p>
            </div>
            <ParentInvoicesSection />
          </>
        )}

        {activeTab === 'bookings' && (
          <>
            <div className="page-header">
              <h1>My Bookings</h1>
              <p>View and manage your confirmed bookings</p>
            </div>

            {/* Outstanding Balance card — full itemised prompt when the
                parent has ≥1 unpaid bookings. Groups every unpaid row
                into one payment intent so they can clear the balance
                in a single card charge. */}
            <OutstandingBalanceCard
              bookings={bookings}
              onSettle={handleSettleOutstanding}
              settling={settlingOutstanding}
            />

            {loading ? (
              <div>Loading...</div>
            ) : bookings.length > 0 ? (
              <div className="card">
                <table>
                  <thead>
                    <tr>
                      <th>Child</th>
                      <th>Session</th>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Price</th>
                      <th>Status</th>
                      <th>Payment</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map(booking => {
                      const ps = booking.payment_status;
                      const psLabel = {
                        paid: { text: '✓ Paid', bg: 'hsl(140 70% 92%)', fg: 'hsl(140 55% 26%)' },
                        pending_payment: { text: 'Payment needed', bg: 'hsl(45 95% 92%)', fg: 'hsl(35 80% 30%)' },
                        pending_mollie: { text: 'Card in progress', bg: 'hsl(220 60% 94%)', fg: 'hsl(220 60% 30%)' },
                        pending_bank_transfer: { text: `Bank transfer${booking.payment_reference ? ` · ${booking.payment_reference}` : ''}`, bg: 'hsl(35 90% 92%)', fg: 'hsl(30 80% 30%)' },
                        not_required: null,
                      }[ps] || null;
                      const needsPayment = ps === 'pending_payment' || ps === 'pending_bank_transfer' || ps === 'pending_mollie';
                      return (
                        <tr key={booking._id} data-testid={`parent-booking-row-${booking._id}`}>
                          <td>{booking.child_name}</td>
                          <td>{booking.session_name}</td>
                          <td>{new Date(booking.session_date).toLocaleDateString()}</td>
                          <td>{booking.start_time} - {booking.end_time}</td>
                          <td>£{booking.price_paid}</td>
                          <td><span className={`badge ${booking.status === 'cancelled' ? 'badge-red' : 'badge-green'}`}>{booking.status}</span></td>
                          <td>
                            {psLabel ? (
                              <span
                                className="badge"
                                style={{ background: psLabel.bg, color: psLabel.fg, fontSize: '0.72rem', padding: '0.15rem 0.5rem', borderRadius: 999, fontWeight: 600 }}
                                data-testid={`parent-booking-payment-badge-${booking._id}`}
                              >
                                {psLabel.text}
                              </span>
                            ) : (
                              <span style={{ color: 'hsl(var(--muted-foreground))', fontSize: '0.8rem' }}>—</span>
                            )}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                              {booking.status !== 'cancelled' && needsPayment && (
                                <button
                                  type="button"
                                  className="btn btn-primary"
                                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.55rem' }}
                                  onClick={() => setActivePayment({
                                    _id: booking._id,
                                    payment_amount: booking.price_paid,
                                    child_name: booking.child_name,
                                  })}
                                  data-testid={`parent-booking-pay-${booking._id}`}
                                >
                                  💳 Pay
                                </button>
                              )}
                              {booking.status !== 'cancelled' && (
                                <RowActions
                                  testIdPrefix={`booking-${booking._id}`}
                                  onDelete={async () => { await api.cancelBooking(booking._id); loadData(); }}
                                  dangerLabel="Cancel"
                                  confirmTitle="Cancel this booking?"
                                  confirmText="Your booking will be cancelled. Refund/credit policies depend on your provider's terms."
                                />
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">No bookings yet</div>
            )}
          </>
        )}

        {activeTab === 'plans' && (
          <>
            <div className="page-header">
              <h1>🔁 Recurring booking plans</h1>
              <p>Set up an All-Year-Round, Term-Time-Only or Ad-hoc schedule once and skip the click-through.</p>
            </div>
            <EnrollmentPlanManager mode="parent" />
          </>
        )}

        {activeTab === 'meals' && (
          <>
            <div className="page-header">
              <h1>🍽️ Weekly Menu</h1>
              <p>What's being served at your provider this week. Allergy warnings appear automatically when a meal contains something one of your children is allergic to.</p>
            </div>
            <MealsManager mode="parent" />
          </>
        )}

        {activeTab === 'waitlist' && (
          <>
            <div className="page-header">
              <h1>Waitlist</h1>
              <p>Track your waitlist positions</p>
            </div>

            {loading ? (
              <div>Loading...</div>
            ) : waitlist.length > 0 ? (
              <div className="card">
                <table>
                  <thead>
                    <tr>
                      <th>Session</th>
                      <th>Date</th>
                      <th>Position</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {waitlist.map(entry => (
                      <tr key={entry._id}>
                        <td>{entry.session_name}</td>
                        <td>{new Date(entry.session_date).toLocaleDateString()}</td>
                        <td>#{entry.position}</td>
                        <td>
                          <span className={`badge ${
                            entry.status === 'waiting' ? 'badge-yellow' :
                            entry.status === 'offered' ? 'badge-green' :
                            entry.status === 'expired' ? 'badge-red' :
                            'badge-blue'
                          }`}>
                            {entry.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">No waitlist entries</div>
            )}
          </>
        )}

        {activeTab === 'moments' && (
          <>
            <div className="page-header">
              <h1>Moments Feed</h1>
              <p>Special moments from your children's day</p>
            </div>

            {loading ? (
              <div className="loading">Loading moments...</div>
            ) : moments.length > 0 ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '1.5rem' }}>
                {moments.map(moment => {
                  const child = children.find(c => c._id === moment.child_id);
                  return (
                    <div
                      key={moment._id}
                      className="card"
                      style={{ overflow: 'hidden', cursor: 'pointer' }}
                      role="button"
                      tabIndex={0}
                      onClick={() => setViewingMoment(moment)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setViewingMoment(moment);
                        }
                      }}
                      data-testid={`moment-${moment._id}`}
                    >
                      {moment.photo_url && (
                        moment.photo_content_type?.startsWith('video/') ? (
                          <video
                            src={moment.photo_url}
                            preload="metadata"
                            muted
                            playsInline
                            style={{
                              width: '100%',
                              height: '220px',
                              objectFit: 'cover',
                              borderRadius: '6px 6px 0 0',
                              marginBottom: '1rem',
                              background: '#000',
                              display: 'block',
                            }}
                          />
                        ) : (
                          <img
                            src={moment.photo_url}
                            alt={moment.caption}
                            style={{
                              width: '100%',
                              height: '220px',
                              objectFit: 'cover',
                              borderRadius: '6px 6px 0 0',
                              marginBottom: '1rem'
                            }}
                          />
                        )
                      )}
                      <div style={{ padding: moment.photo_url ? '0 1rem 1rem' : '1rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                          <div>
                            {child && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                <span className="badge badge-blue">{child.first_name} {child.last_name}</span>
                              </div>
                            )}
                            <p
                              style={{
                                margin: 0,
                                fontSize: '0.95rem',
                                lineHeight: '1.5',
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {moment.caption}
                            </p>
                          </div>
                        </div>

                        {moment.tags && moment.tags.length > 0 && (
                          <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
                            {moment.tags.map((tag, i) => (
                              <span key={i} className="badge badge-green" style={{ fontSize: '0.75rem' }}>
                                #{tag}
                              </span>
                            ))}
                          </div>
                        )}

                        <div style={{ 
                          marginTop: '1rem', 
                          paddingTop: '0.75rem', 
                          borderTop: '1px solid hsl(var(--border))',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
                            {moment.posted_by_name && <span>By {moment.posted_by_name}</span>}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
                            {moment.comment_count > 0 && (
                              <span data-testid={`moment-card-comment-count-${moment._id}`}>
                                💬 {moment.comment_count}
                              </span>
                            )}
                            <span>{new Date(moment.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state">
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📸</div>
                  <p>No moments shared yet</p>
                  <p style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>
                    Your child's educators will share special moments here
                  </p>
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'notifications' && (
          <>
            <div className="page-header">
              <h1>Notifications</h1>
              <p>Stay updated with important alerts</p>
            </div>

            {loading ? (
              <div>Loading...</div>
            ) : notifications.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {notifications.map(notification => (
                  <div 
                    key={notification._id} 
                    className="card"
                    style={{ 
                      background: notification.is_read ? 'hsl(var(--card))' : 'hsl(var(--accent))',
                      borderLeft: '4px solid hsl(var(--primary))'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div>
                        <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1.125rem' }}>
                          {notification.title}
                        </h3>
                        <p style={{ margin: '0 0 0.5rem 0' }}>{notification.message}</p>
                        <small style={{ color: 'hsl(var(--muted-foreground))' }}>
                          {new Date(notification.created_at).toLocaleString()}
                        </small>
                      </div>
                      {notification.type === 'waitlist_offer' && !notification.is_read && (
                        <button 
                          className="btn btn-primary"
                          onClick={() => handleClaimWaitlist(notification)}
                          data-testid={`claim-waitlist-${notification._id}`}
                        >
                          Claim Spot
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">No notifications</div>
            )}
          </>
        )}

        {activeTab === 'messages' && (
          <MessagesInbox
            user={user}
            onCompose={() => {
              // Open the shared composer. If the parent has exactly one
              // child we pre-select it so they don't have to pick again.
              if (children.length === 1) {
                const c = children[0];
                setComposerCtx({
                  childId: c._id,
                  childName: `${c.first_name} ${c.last_name}`.trim(),
                });
              } else {
                setComposerCtx({ childId: null, childName: null });
              }
            }}
          />
        )}

        {activeTab === 'policies' && (
          <>
            <div className="page-header">
              <h1>Policies</h1>
              <p>Fees, cancellation, safeguarding and other nursery policies</p>
            </div>
            <PoliciesTab />
          </>
        )}

        {activeTab === 'account' && (
          <>
            <div className="page-header">
              <h1>Account</h1>
              <p>Manage your account and payments</p>
            </div>

            {loading ? (
              <div>Loading...</div>
            ) : (
              <div className="card-grid">
                <div
                  className="stat-card"
                  onClick={() => setActiveTab('account')}
                  data-testid="parent-stat-balance"
                  style={{ cursor: 'pointer', transition: 'transform 0.15s ease, box-shadow 0.15s ease' }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 18px hsl(210 40% 20% / 0.08)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = ''; }}
                  title="Open your account statement"
                >
                  <h3>Account Balance</h3>
                  <p className="value" style={{ color: balance?.balance > 0 ? 'hsl(var(--destructive))' : 'hsl(var(--primary))' }}>
                    £{balance?.balance.toFixed(2) || '0.00'}
                  </p>
                  <small style={{ color: 'hsl(var(--muted-foreground))' }}>
                    {balance?.balance > 0 ? 'Amount due — tap to view' : 'Credit balance — tap to view'}
                  </small>
                </div>
                <div
                  className="stat-card"
                  onClick={() => setActiveTab('children')}
                  data-testid="parent-stat-children"
                  style={{ cursor: 'pointer', transition: 'transform 0.15s ease, box-shadow 0.15s ease' }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 18px hsl(210 40% 20% / 0.08)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = ''; }}
                  title="Manage your children"
                >
                  <h3>Total Children</h3>
                  <p className="value">{children.length}</p>
                  <small style={{ color: 'hsl(var(--muted-foreground))' }}>Tap to manage →</small>
                </div>
                <div
                  className="stat-card"
                  onClick={() => setActiveTab('bookings')}
                  data-testid="parent-stat-bookings"
                  style={{ cursor: 'pointer', transition: 'transform 0.15s ease, box-shadow 0.15s ease' }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 18px hsl(210 40% 20% / 0.08)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = ''; }}
                  title="See your bookings"
                >
                  <h3>Active Bookings</h3>
                  <p className="value">{bookings.length}</p>
                  <small style={{ color: 'hsl(var(--muted-foreground))' }}>Tap to view →</small>
                </div>
              </div>
            )}

            {/* Per-user email notification toggles. Honoured by the
                backend email_service whenever we send broadcasts,
                booking confirmations, or billing receipts. */}
            <div style={{ marginTop: '1.5rem' }}>
              <EmailPreferencesCard />
            </div>

            {/* Fine-grained per-event channel picker. Lets the parent
                pick push, email or both for moment replies + payment
                reminders. Honoured by the moment-comment endpoint and
                the payment-reminder scheduler. */}
            <div style={{ marginTop: '1.5rem' }}>
              <NotificationPreferencesCard />
            </div>
          </>
        )}
      </div>

      {/* Add/Edit Child Modal */}
      {showChildModal && editingChild && (
        <div className="modal-overlay" onClick={closeChildModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Child</h2>
            <form onSubmit={handleCreateChild}>
              {/* Photo picker — uploads to S3 on submit and stores
                  photo_file_id so the server can refresh presigned URLs. */}
              <div className="form-group" data-testid="child-photo-uploader">
                <label>Profile Photo</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.25rem' }}>
                  <div
                    style={{
                      width: '72px',
                      height: '72px',
                      borderRadius: '50%',
                      overflow: 'hidden',
                      flex: '0 0 auto',
                      background: 'hsl(var(--muted))',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid hsl(var(--border))',
                    }}
                  >
                    {(childPhotoPreview || editingChild?.photo_url) ? (
                      <img
                        src={childPhotoPreview || editingChild.photo_url}
                        alt="Child preview"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        data-testid="child-photo-preview"
                      />
                    ) : (
                      <span style={{ fontSize: '1.5rem', color: 'hsl(var(--muted-foreground))' }} aria-hidden="true">👶</span>
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={handleChildPhotoPick}
                      data-testid="child-photo-input"
                      style={{ display: 'block', width: '100%' }}
                    />
                    <small style={{ color: 'hsl(var(--muted-foreground))' }}>
                      PNG, JPEG or WebP — max 5 MB.
                      {childPhotoUploading && <> Uploading…</>}
                    </small>
                  </div>
                  {(childPhotoPreview || (editingChild && editingChild.photo_url)) && (
                    <button
                      type="button"
                      className="btn btn-ghost"
                      onClick={resetChildPhotoState}
                      data-testid="child-photo-clear"
                      style={{ flex: '0 0 auto' }}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label>First Name</label>
                <input name="first_name" required defaultValue={editingChild?.first_name || ''} data-testid="child-first-name" />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input name="last_name" required defaultValue={editingChild?.last_name || ''} data-testid="child-last-name" />
              </div>
              <div className="form-group">
                <label>Date of Birth</label>
                <input name="date_of_birth" type="date" required
                  defaultValue={editingChild?.date_of_birth ? String(editingChild.date_of_birth).slice(0, 10) : ''}
                  data-testid="child-dob" />
              </div>
              <div className="form-group">
                <label>Age Group</label>
                <select name="age_group" required defaultValue={editingChild?.age_group || 'preschool'} data-testid="child-age-group">
                  <option value="infant">Infant (0-2)</option>
                  <option value="toddler">Toddler (2-3)</option>
                  <option value="preschool">Preschool (3-5)</option>
                  <option value="primary">Primary (5-11)</option>
                </select>
              </div>
              <div className="form-group">
                <label>Allergies (comma separated)</label>
                <input name="allergies" defaultValue={(editingChild?.allergies || []).join(', ')} data-testid="child-allergies" />
              </div>

              {/* Structured SEND + dietary picker — same component as the
                  Enrollment Wizard so the experience is identical. */}
              <SpecialNeedsAndDietPicker
                dietary={childDietary}
                onDietaryChange={setChildDietary}
                specialNeeds={childSpecialNeeds}
                onSpecialNeedsChange={setChildSpecialNeeds}
                compact
                testIdPrefix="quick-"
              />

              <div className="form-group">
                <label>Medical Notes</label>
                <textarea name="medical_notes" rows={2} defaultValue={editingChild?.medical_notes || ''} data-testid="child-medical-notes" />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={closeChildModal}>Cancel</button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  data-testid="submit-child"
                  disabled={childPhotoUploading}
                >
                  {childPhotoUploading
                    ? 'Uploading photo…'
                    : (editingChild ? 'Save changes' : 'Add Child')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Enrollment Wizard */}
      {showEnrollmentWizard && (
        <EnrollmentWizard 
          user={user}
          onComplete={handleEnrollmentComplete}
          onCancel={() => setShowEnrollmentWizard(false)}
        />
      )}

      {/* Shared MessageComposer — opened by "Message nursery" buttons */}
      <MessageComposer
        open={!!composerCtx}
        onClose={() => setComposerCtx(null)}
        user={user}
        childId={composerCtx?.childId || null}
        childName={composerCtx?.childName || null}
        onSent={() => {
          setComposerCtx(null);
          // Re-trigger loadData so unread counts + inbox stay in sync.
          loadData();
        }}
      />

      {/* PAYG booking payment dialog — opens after a successful booking
          if the parent's child is on pay_as_you_go / standard billing.
          When the parent finishes / cancels the current booking, we
          pop the next queued booking (siblings booked in one batch may
          have their own payments to make). */}
      <BookingPaymentDialog
        open={!!activePayment}
        booking={activePayment}
        childName={activePayment?.child_name}
        onClose={() => {
          setActivePayment(null);
          setPaymentQueue((prev) => {
            if (prev.length === 0) return prev;
            const [next, ...rest] = prev;
            setActivePayment(next);
            return rest;
          });
          // Refresh bookings so status badges reflect any pending state.
          loadData();
        }}
      />

      {/* Grouped payment review dialog — opens after a multi-booking batch
          (e.g. 3 kids × 5 sessions) and charges the parent ONCE via Mollie
          with an itemised line-item summary. Replaces the old N-per-batch
          card popups. */}
      <PaymentReviewDialog
        open={!!pendingIntent}
        intent={pendingIntent}
        onClose={() => {
          setPendingIntent(null);
          loadData();
        }}
        onPaid={() => {
          setPendingIntent(null);
          loadData();
        }}
      />

      {/* Parent moment detail modal — clicks a card to see the full media,
          full caption and reply thread. Reuses the shared MomentComments
          component so parents & staff/admin see the same conversation. */}
      {viewingMoment && (() => {
        const vm = viewingMoment;
        const isVideo = (vm.photo_content_type || '').startsWith('video/');
        return (
          <div
            className="modal-overlay"
            onClick={() => setViewingMoment(null)}
            data-testid="parent-moment-detail-modal"
          >
            <div
              className="modal"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: 720, width: '100%' }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <div>
                  <h2 style={{ margin: 0 }}>📸 Moment</h2>
                  <small style={{ color: 'hsl(var(--muted-foreground))' }}>
                    {vm.child_name || 'Your child'} • posted by {vm.posted_by_name || 'staff'} •{' '}
                    {new Date(vm.created_at).toLocaleString()}
                    {vm.edited_at && (
                      <>
                        {' • '}
                        <span style={{ fontStyle: 'italic' }}>edited {new Date(vm.edited_at).toLocaleString()}</span>
                      </>
                    )}
                  </small>
                </div>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => setViewingMoment(null)}
                  aria-label="Close moment detail"
                  data-testid="parent-moment-detail-close"
                >
                  ✕
                </button>
              </div>

              {vm.photo_url ? (
                <div style={{ margin: '0.75rem 0', background: '#000', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
                  {isVideo ? (
                    <video
                      src={vm.photo_url}
                      controls
                      preload="metadata"
                      data-testid="parent-moment-detail-video"
                      style={{ width: '100%', maxHeight: '60vh', display: 'block', background: '#000' }}
                    />
                  ) : (
                    <img
                      src={vm.photo_url}
                      alt={vm.caption || 'Moment media'}
                      data-testid="parent-moment-detail-image"
                      style={{ width: '100%', maxHeight: '60vh', objectFit: 'contain', background: '#000', display: 'block' }}
                    />
                  )}
                </div>
              ) : null}

              {vm.caption && (
                <p
                  style={{ margin: '0.5rem 0', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}
                  data-testid="parent-moment-detail-caption"
                >
                  {vm.caption}
                </p>
              )}

              {vm.tags && vm.tags.length > 0 && (
                <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', margin: '0.5rem 0 0.75rem' }}>
                  {vm.tags.map((tag, i) => (
                    <span key={i} className="badge badge-green">#{tag}</span>
                  ))}
                </div>
              )}

              <MomentComments
                momentId={vm._id}
                currentUser={user}
                onCountChange={(n) => {
                  setMoments((prev) => (prev || []).map((x) => (x._id === vm._id ? { ...x, comment_count: n } : x)));
                  setViewingMoment((cur) => (cur && cur._id === vm._id ? { ...cur, comment_count: n } : cur));
                }}
              />

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setViewingMoment(null)}
                  data-testid="parent-moment-detail-done"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default ParentDashboard;

// ---------------------------------------------------------------------------
// OutstandingBalanceBadge — compact nudge on the Children tab so parents
// notice unpaid bookings without switching tabs. Clicking it jumps them
// straight to the Bookings tab where they can settle in one go.
// ---------------------------------------------------------------------------
function OutstandingBalanceBadge({ bookings, onSettle, settling, onJump }) {
  const unpaid = (bookings || []).filter((b) => b.payment_status === 'pending_payment');
  if (unpaid.length === 0) return null;
  const total = unpaid.reduce((s, b) => s + Number(b.price_paid || b.payment_amount || 0), 0);
  return (
    <div
      data-testid="outstanding-balance-badge"
      style={{
        display: 'flex', alignItems: 'center', gap: '0.75rem',
        background: 'hsl(45 95% 96%)', border: '1px solid hsl(38 88% 78%)',
        borderRadius: 8, padding: '0.6rem 0.85rem', marginBottom: '1rem',
        fontSize: '0.88rem',
      }}
    >
      <span style={{ fontSize: '1.1rem' }}>💳</span>
      <div style={{ flex: 1 }}>
        <strong data-testid="outstanding-badge-title">
          {unpaid.length} unpaid booking{unpaid.length === 1 ? '' : 's'} — £{total.toFixed(2)}
        </strong>
        <div style={{ fontSize: '0.75rem', color: 'hsl(30 60% 30%)' }}>
          Clear the whole balance in one card charge from the Bookings tab.
        </div>
      </div>
      <button type="button" className="btn btn-primary"
        onClick={onSettle} disabled={settling}
        data-testid="outstanding-badge-pay"
        style={{ fontSize: '0.78rem', padding: '0.35rem 0.7rem' }}>
        {settling ? 'Loading…' : `Pay £${total.toFixed(2)}`}
      </button>
      <button type="button" className="btn btn-ghost"
        onClick={onJump}
        data-testid="outstanding-badge-jump"
        style={{ fontSize: '0.78rem', padding: '0.35rem 0.6rem' }}>
        View →
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// OutstandingBalanceCard — full itemised card on the Bookings tab. Lists
// every unpaid booking, shows the total, and offers a single "Pay all" CTA
// that bundles the whole basket into ONE payment intent.
// ---------------------------------------------------------------------------
function OutstandingBalanceCard({ bookings, onSettle, settling }) {
  const unpaid = (bookings || []).filter((b) => b.payment_status === 'pending_payment');
  if (unpaid.length === 0) return null;
  const total = unpaid.reduce((s, b) => s + Number(b.price_paid || b.payment_amount || 0), 0);
  return (
    <div
      className="card"
      data-testid="outstanding-balance-card"
      style={{
        borderLeft: '4px solid hsl(38 88% 55%)',
        background: 'hsl(45 95% 97%)',
        marginBottom: '1.25rem',
        padding: '1rem 1.15rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h3 style={{ margin: 0, fontSize: '1.05rem', color: 'hsl(30 65% 25%)' }}
              data-testid="outstanding-card-title">
            💳 You have {unpaid.length} unpaid booking{unpaid.length === 1 ? '' : 's'}
          </h3>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: 'hsl(30 40% 30%)' }}>
            Pay <strong>£{total.toFixed(2)}</strong> in one go — one card charge, one receipt, one line on your statement.
          </p>
        </div>
        <button type="button" className="btn btn-primary"
          onClick={onSettle} disabled={settling}
          data-testid="outstanding-card-pay"
          style={{ whiteSpace: 'nowrap' }}>
          {settling ? 'Loading…' : `Pay all £${total.toFixed(2)}`}
        </button>
      </div>
      <ul style={{ listStyle: 'none', margin: '0.85rem 0 0', padding: 0 }}>
        {unpaid.map((b) => (
          <li key={b._id}
              data-testid={`outstanding-card-row-${b._id}`}
              style={{
                display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto',
                gap: '0.5rem', padding: '0.4rem 0',
                borderTop: '1px solid hsl(38 60% 88%)',
                fontSize: '0.85rem',
              }}>
            <span>{b.child_name} · {b.session_name} · {b.session_date ? new Date(b.session_date).toLocaleDateString() : ''}</span>
            <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>£{Number(b.price_paid || b.payment_amount || 0).toFixed(2)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// FirstRunChecklist — a friendly 3-step "getting started" card shown to
// freshly-registered parents. Auto-progresses as they complete each step:
//   1. Register (always done by the time this renders)
//   2. Add your first child
//   3. Book your first session
// Fully self-contained (module scope so we don't blow up the main
// component's render tree). Purely presentational — state is driven by
// props from ParentDashboard.
// ---------------------------------------------------------------------------
function FirstRunChecklist({
  userName,
  organizationName,
  hasChild,
  hasBooking,
  onAddChild,
  onBook,
  onDismiss,
}) {
  const firstName = (userName || '').split(' ')[0] || 'there';
  const steps = [
    {
      key: 'register',
      icon: '👤',
      title: 'Create your account',
      done: true,
      hint: 'Done ✓',
      cta: null,
    },
    {
      key: 'child',
      icon: '👶',
      title: 'Add your child',
      done: hasChild,
      hint: hasChild
        ? 'Nicely done — profile saved.'
        : 'Takes about 3 minutes. You\'ll be asked to accept the terms and sign a short parent declaration.',
      cta: hasChild ? null : { label: 'Add Your Child →', onClick: onAddChild, testid: 'first-run-add-child' },
    },
    {
      key: 'book',
      icon: '📅',
      title: 'Book your first session',
      done: hasBooking,
      hint: hasBooking
        ? 'Booked ✓ — see it under My Bookings.'
        : (hasChild
          ? 'Pick any upcoming session and reserve their place.'
          : 'Available after your child is added.'),
      cta: hasBooking
        ? null
        : hasChild
          ? { label: 'Browse Sessions →', onClick: onBook, testid: 'first-run-book' }
          : null,
    },
  ];
  const completed = steps.filter((s) => s.done).length;
  const total = steps.length;
  const percent = Math.round((completed / total) * 100);
  const allDone = completed === total;

  return (
    <div
      data-testid="parent-first-run-checklist"
      style={{
        background: 'linear-gradient(135deg, hsl(210 70% 98%) 0%, hsl(190 65% 97%) 100%)',
        border: '1px solid hsl(190 60% 82%)',
        borderRadius: 12,
        padding: '1.05rem 1.2rem',
        marginBottom: '1.25rem',
        boxShadow: '0 2px 8px hsl(190 40% 90% / 0.5)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem', flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ margin: '0 0 0.15rem 0', fontSize: '1.1rem', color: 'hsl(200 60% 22%)' }}>
            👋 Welcome, {firstName}!
          </h3>
          <p style={{ margin: 0, fontSize: '0.85rem', color: 'hsl(200 30% 35%)' }}>
            {allDone
              ? "You're all set — welcome to " + (organizationName || 'the family') + '!'
              : hasChild
                ? "Great start! Just one more step and you're set."
                : "Let's get you set up in a couple of quick steps."}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div
            style={{
              background: 'white',
              border: '1px solid hsl(190 40% 80%)',
              borderRadius: 999,
              padding: '0.3rem 0.7rem',
              fontSize: '0.78rem',
              fontWeight: 600,
              color: 'hsl(200 60% 25%)',
            }}
            data-testid="first-run-progress"
          >
            {completed}/{total} · {percent}%
          </div>
          {allDone && (
            <button
              type="button"
              onClick={onDismiss}
              className="btn btn-ghost"
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.7rem' }}
              data-testid="first-run-dismiss"
              title="Hide this getting-started card"
            >
              Dismiss
            </button>
          )}
        </div>
      </div>

      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        style={{
          height: 6,
          background: 'hsl(190 40% 92%)',
          borderRadius: 999,
          overflow: 'hidden',
          margin: '0.75rem 0 0.9rem 0',
        }}
      >
        <div
          style={{
            width: `${percent}%`,
            height: '100%',
            background: 'linear-gradient(90deg, hsl(190 70% 55%) 0%, hsl(150 65% 50%) 100%)',
            transition: 'width 250ms ease',
          }}
        />
      </div>

      <div style={{ display: 'grid', gap: '0.5rem' }}>
        {steps.map((step) => (
          <div
            key={step.key}
            data-testid={`first-run-step-${step.key}`}
            data-done={step.done ? 'true' : 'false'}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.65rem',
              padding: '0.5rem 0.7rem',
              background: step.done ? 'hsl(140 55% 96%)' : 'white',
              border: `1px solid ${step.done ? 'hsl(140 50% 80%)' : 'hsl(210 25% 88%)'}`,
              borderRadius: 8,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                background: step.done ? 'hsl(140 55% 45%)' : 'hsl(210 20% 82%)',
                color: 'white',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.85rem',
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {step.done ? '✓' : step.icon}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  color: step.done ? 'hsl(140 40% 25%)' : 'hsl(210 25% 20%)',
                  textDecoration: step.done ? 'line-through' : 'none',
                }}
              >
                {step.title}
              </div>
              {step.hint && (
                <div style={{ fontSize: '0.72rem', color: 'hsl(210 15% 45%)', marginTop: 2 }}>
                  {step.hint}
                </div>
              )}
            </div>
            {step.cta && (
              <button
                type="button"
                onClick={step.cta.onClick}
                className="btn btn-primary"
                style={{ fontSize: '0.75rem', padding: '0.3rem 0.75rem', whiteSpace: 'nowrap' }}
                data-testid={step.cta.testid}
              >
                {step.cta.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
